import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/primitives/avatar";
import { Button } from "@/components/primitives/button";
import { cn, stringToRgbColor } from "@/lib/utils";
import { SearchableGroupChatResponse } from "@/server/types/chat";
import { Rss } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogTrigger,
} from "@/components/primitives/dialog";

import { useToast } from "@/hooks/useToast";

import { joinPublicGroupChat } from "@/middleware/chats";

export default function PublicChatListItem({
  chat,
  isRequestPending,
}: {
  chat: SearchableGroupChatResponse;
  isRequestPending: boolean;
}) {
  const avatarColor = useMemo(() => stringToRgbColor(chat.id), [chat.id]); // RGB format
  const { t } = useTranslation();

  const { toast } = useToast();

  const handleJoin = async () => {
    try {
      await joinPublicGroupChat(chat.id);

      toast({
        duration: 1500,
        description: t("joinRequest.sent"),
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="relative flex w-full cursor-pointer items-center gap-2 overflow-hidden rounded-md p-2 lg:hover:bg-muted">
      <Avatar
        aria-label={t("aria.chatAvatar")}
        style={{ backgroundColor: `rgba(${avatarColor}, 0.7)` }}
        className={cn("h-10 w-10 text-sm")}
      >
        <AvatarImage src={chat.picture} />
        <AvatarFallback className="bg-transparent">
          {chat.name.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="line-clamp-1 flex items-center gap-2 overflow-hidden">
          <Rss size={14} className="shrink-0" />

          <h4 className="text-md truncate font-semibold leading-tight">
            {chat.name}
          </h4>
        </div>
        <h6 className="truncate text-sm font-normal leading-tight">
          {chat.description}
        </h6>
      </div>

      <Dialog>
        <DialogTrigger asChild>
          {isRequestPending ? (
            <Button disabled className="bg-muted">
              {t("pending")}
            </Button>
          ) : (
            <Button>{t("join")}</Button>
          )}
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle />
            <DialogDescription />
          </DialogHeader>

          <div className="flex min-w-0 flex-col items-center justify-center">
            <Avatar
              aria-label={t("aria.chatAvatar")}
              style={{ backgroundColor: `rgba(${avatarColor}, 0.7)` }}
              className={cn("text-md mb-5 h-20 w-20")}
            >
              <AvatarImage src={chat.picture} />
              <AvatarFallback className="bg-transparent">
                {chat.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>

            <h2 className="line-clamp-1 truncate text-lg font-semibold leading-tight">
              {chat.name}
            </h2>
            <h4 className="line-clamp-2 text-sm font-normal leading-tight text-muted-2">
              {chat.description}
            </h4>
            <DialogClose asChild>
              <Button className="my-7 h-12 w-full" onClick={handleJoin}>
                {t("groupChat.requestToJoinGroup")}
              </Button>
            </DialogClose>

            <p className="mt-2 text-center text-sm opacity-50">
              {t("groupChat.requestToJoinGroupDescription")}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
